/**
 * Enum Formatting Utilities
 *
 * Convention:
 * - Database: lowercase_snake_case
 * - Display: Title Case with spaces
 *
 * This module provides centralized formatting functions for all enum values
 * to ensure consistent display across the application.
 */

/**
 * Convert snake_case or lowercase enum to Title Case display format
 */
function formatEnumValue(
  value: string | null | undefined,
  defaultValue = "Unknown",
): string {
  if (!value) return defaultValue;

  return value
    .toLowerCase()
    .replace(/_/g, " ")
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

/**
 * Format company status for display
 * Database values: suspect, target, prospect, customer, former_customer, lost
 * Display values: Suspect, Target, Prospect, Customer, Former Customer, Lost
 */
export function formatCompanyStatus(status: string | null | undefined): string {
  return formatEnumValue(status, "Suspect");
}

/**
 * Format task status for display
 * Database values: not_started, in_progress, complete, blocked
 * Display values: Not Started, In Progress, Complete, Blocked
 */
export function formatTaskStatus(status: string | null | undefined): string {
  return formatEnumValue(status, "Not Started");
}

/**
 * Format focus mode for display
 * Database values: focus, explore, opportunistic, maintain, exit, none, not_relevant
 * Display values: Focus, Explore, Opportunistic, Maintain, Exit, None, Not Relevant
 */
export function formatFocusMode(mode: string | null | undefined): string {
  return formatEnumValue(mode, "Not Set");
}

/**
 * Format OKR status for display
 * Database values: draft, active, achieved, failed, cancelled
 * Display values: Draft, Active, Achieved, Failed, Cancelled
 */
export function formatOkrStatus(status: string | null | undefined): string {
  return formatEnumValue(status, "Draft");
}

/**
 * Format OKR commitment type for display
 * Database values: committed, aspirational
 * Display values: Committed, Aspirational
 */
export function formatCommitmentType(type: string | null | undefined): string {
  return formatEnumValue(type, "Committed");
}

/**
 * Format opportunity status for display
 * Database values: open, won, lost
 * Display values: Open, Won, Lost
 */
export function formatOpportunityStatus(
  status: string | null | undefined,
): string {
  return formatEnumValue(status, "Open");
}

/**
 * Format opportunity stage for display
 * Database values: discovery, qualification, proposal, negotiation, closed_won, closed_lost
 * Display values: Discovery, Qualification, Proposal, Negotiation, Closed Won, Closed Lost
 */
export function formatOpportunityStage(
  stage: string | null | undefined,
): string {
  return formatEnumValue(stage, "Discovery");
}

/**
 * Format user role for display
 * Database values: viewer, editor, admin
 * Display values: Viewer, Editor, Admin
 */
export function formatUserRole(role: string | null | undefined): string {
  return formatEnumValue(role, "Viewer");
}

/**
 * Format plan type for display
 * Database values: starter, growth, enterprise
 * Display values: Starter, Growth, Enterprise
 */
export function formatPlanType(plan: string | null | undefined): string {
  return formatEnumValue(plan, "Starter");
}

/**
 * Format priority level for display
 * Database values: low, medium, high, critical
 * Display values: Low, Medium, High, Critical
 */
export function formatPriority(priority: string | null | undefined): string {
  return formatEnumValue(priority, "Not Set");
}

/**
 * Format segment priority for display
 * Database values: high, medium, low
 * Display values: High, Medium, Low
 */
export function formatSegmentPriority(
  priority: string | null | undefined,
): string {
  return formatEnumValue(priority, "Not Set");
}

/**
 * Format market maturity for display
 * Database values: emerging, growing, mature, declining
 * Display values: Emerging, Growing, Mature, Declining
 */
export function formatMarketMaturity(
  maturity: string | null | undefined,
): string {
  return formatEnumValue(maturity, "Not Set");
}

/**
 * Format area for display
 * Database values: product, sales, marketing, operations, finance, hr, technology, other
 * Display values: Product, Sales, Marketing, Operations, Finance, HR, Technology, Other
 *
 * Special case: HR is all caps
 */
export function formatArea(area: string | null | undefined): string {
  if (!area) return "Other";

  // Special case for HR
  if (area.toLowerCase() === "hr") {
    return "HR";
  }

  return formatEnumValue(area, "Other");
}

/**
 * Format proposition type for display
 * Database values: product, service, solution, capability
 * Display values: Product, Service, Solution, Capability
 */
export function formatPropositionType(type: string | null | undefined): string {
  return formatEnumValue(type, "Service");
}

/**
 * Format current capability for display
 * Database values: idea, concept, foundation, developing, proven, mature, retiring
 * Display values: Idea, Concept, Foundation, Developing, Proven, Mature, Retiring
 */
export function formatCurrentCapability(
  capability: string | null | undefined,
): string {
  return formatEnumValue(capability, "Foundation");
}

/**
 * Format pricing type for display
 * Database values: subscription, one_time, usage_based, tiered, custom
 * Display values: Subscription, One Time, Usage Based, Tiered, Custom
 */
export function formatPricingType(type: string | null | undefined): string {
  return formatEnumValue(type, "Subscription");
}

/**
 * Format pricing frequency for display
 * Database values: one_time, daily, weekly, monthly, quarterly, annual, custom
 * Display values: One Time, Daily, Weekly, Monthly, Quarterly, Annual, Custom
 */
export function formatPricingFrequency(
  frequency: string | null | undefined,
): string {
  return formatEnumValue(frequency, "Monthly");
}

/**
 * Format user type importance for display
 * Database values: critical, important, nice_to_have
 * Display values: Critical, Important, Nice To Have
 */
export function formatUserTypeImportance(
  importance: string | null | undefined,
): string {
  return formatEnumValue(importance, "Important");
}

/**
 * Format role type for display
 * Database values: buyer, influencer, decision_maker, user, gatekeeper, champion
 * Display values: Buyer, Influencer, Decision Maker, User, Gatekeeper, Champion
 */
export function formatRoleType(role: string | null | undefined): string {
  return formatEnumValue(role, "User");
}

/**
 * Format notification type for display
 * Database values: task_assigned, task_completed, okr_updated, comment_added
 * Display values: Task Assigned, Task Completed, OKR Updated, Comment Added
 */
export function formatNotificationType(
  type: string | null | undefined,
): string {
  // Special handling for OKR acronym
  if (type?.toLowerCase() === "okr_updated") {
    return "OKR Updated";
  }
  return formatEnumValue(type, "Notification");
}

/**
 * Generic enum formatter for any enum value
 * Use this for enums that don't have special formatting requirements
 */
export function formatEnum(
  value: string | null | undefined,
  defaultValue = "Unknown",
): string {
  return formatEnumValue(value, defaultValue);
}

/**
 * Export all formatters as a namespace for easy access
 */
export const EnumFormatters = {
  companyStatus: formatCompanyStatus,
  taskStatus: formatTaskStatus,
  focusMode: formatFocusMode,
  okrStatus: formatOkrStatus,
  opportunityStage: formatOpportunityStage,
  userRole: formatUserRole,
  planType: formatPlanType,
  priority: formatPriority,
  segmentPriority: formatSegmentPriority,
  marketMaturity: formatMarketMaturity,
  area: formatArea,
  propositionType: formatPropositionType,
  currentCapability: formatCurrentCapability,
  pricingType: formatPricingType,
  pricingFrequency: formatPricingFrequency,
  userTypeImportance: formatUserTypeImportance,
  roleType: formatRoleType,
  notificationType: formatNotificationType,
  generic: formatEnum,
} as const;
